/**
 * Template formazione pre-impostate per sport (spec v1.1.1 sez 9 righe 699-712).
 * Coordinate sono percentuali (0-100) del SVG viewBox, poi mappate a pixel.
 */

export interface FormationTemplate {
  id: string;
  label: string;
  sport: 'soccer' | 'rugby' | 'basket' | 'volleyball' | 'futsal';
  positions: Array<{
    id: string;
    x: number;
    y: number;
    roleLabel: string;
  }>;
}

export const FORMATION_TEMPLATES: FormationTemplate[] = [
  // ─── Calcio ─────────────────────────────────────────────────────────────
  {
    id: 'soccer_4_3_3',
    label: '4-3-3',
    sport: 'soccer',
    positions: [
      { id: 'gk', x: 170, y: 480, roleLabel: 'Portiere' },
      { id: 'rb', x: 290, y: 390, roleLabel: 'Terzino dx' },
      { id: 'cb1', x: 220, y: 410, roleLabel: 'Difensore centrale' },
      { id: 'cb2', x: 120, y: 410, roleLabel: 'Difensore centrale' },
      { id: 'lb', x: 50, y: 390, roleLabel: 'Terzino sx' },
      { id: 'cm1', x: 240, y: 280, roleLabel: 'Mediano dx' },
      { id: 'cm2', x: 170, y: 300, roleLabel: 'Regista' },
      { id: 'cm3', x: 100, y: 280, roleLabel: 'Mediano sx' },
      { id: 'rw', x: 290, y: 140, roleLabel: 'Ala dx' },
      { id: 'st', x: 170, y: 110, roleLabel: 'Centravanti' },
      { id: 'lw', x: 50, y: 140, roleLabel: 'Ala sx' },
    ],
  },
  {
    id: 'soccer_4_4_2',
    label: '4-4-2',
    sport: 'soccer',
    positions: [
      { id: 'gk', x: 170, y: 480, roleLabel: 'Portiere' },
      { id: 'rb', x: 290, y: 390, roleLabel: 'Terzino dx' },
      { id: 'cb1', x: 220, y: 410, roleLabel: 'Difensore centrale' },
      { id: 'cb2', x: 120, y: 410, roleLabel: 'Difensore centrale' },
      { id: 'lb', x: 50, y: 390, roleLabel: 'Terzino sx' },
      { id: 'rm', x: 290, y: 260, roleLabel: 'Esterno dx' },
      { id: 'cm1', x: 220, y: 280, roleLabel: 'Mediano dx' },
      { id: 'cm2', x: 120, y: 280, roleLabel: 'Mediano sx' },
      { id: 'lm', x: 50, y: 260, roleLabel: 'Esterno sx' },
      { id: 'st1', x: 220, y: 120, roleLabel: 'Attaccante' },
      { id: 'st2', x: 120, y: 120, roleLabel: 'Attaccante' },
    ],
  },
  {
    id: 'soccer_3_5_2',
    label: '3-5-2',
    sport: 'soccer',
    positions: [
      { id: 'gk', x: 170, y: 480, roleLabel: 'Portiere' },
      { id: 'cb1', x: 260, y: 410, roleLabel: 'Difensore dx' },
      { id: 'cb2', x: 170, y: 420, roleLabel: 'Libero' },
      { id: 'cb3', x: 80, y: 410, roleLabel: 'Difensore sx' },
      { id: 'rwb', x: 300, y: 280, roleLabel: 'Quinto dx' },
      { id: 'cm1', x: 230, y: 300, roleLabel: 'Mediano' },
      { id: 'cm2', x: 170, y: 280, roleLabel: 'Regista' },
      { id: 'cm3', x: 110, y: 300, roleLabel: 'Mediano' },
      { id: 'lwb', x: 40, y: 280, roleLabel: 'Quinto sx' },
      { id: 'st1', x: 220, y: 120, roleLabel: 'Attaccante' },
      { id: 'st2', x: 120, y: 120, roleLabel: 'Attaccante' },
    ],
  },
  // ─── Rugby ───────────────────────────────────────────────────────────────
  {
    id: 'rugby_standard_15',
    label: 'Rugby 15 standard',
    sport: 'rugby',
    positions: [
      { id: 'lp', x: 100, y: 410, roleLabel: 'Pilone sx (1)' },
      { id: 'h', x: 170, y: 410, roleLabel: 'Tallonatore (2)' },
      { id: 'tp', x: 240, y: 410, roleLabel: 'Pilone dx (3)' },
      { id: 'l1', x: 130, y: 380, roleLabel: 'Seconda linea (4)' },
      { id: 'l2', x: 210, y: 380, roleLabel: 'Seconda linea (5)' },
      { id: 'fl1', x: 90, y: 345, roleLabel: 'Flanker sx (6)' },
      { id: 'fl2', x: 250, y: 345, roleLabel: 'Flanker dx (7)' },
      { id: 'n8', x: 170, y: 350, roleLabel: 'N°8 (8)' },
      { id: 'sh', x: 170, y: 290, roleLabel: 'Mediano di mischia (9)' },
      { id: 'fh', x: 200, y: 250, roleLabel: 'Apertura (10)' },
      { id: 'lw', x: 40, y: 180, roleLabel: 'Ala sx (11)' },
      { id: 'lc', x: 110, y: 220, roleLabel: 'Centro sx (12)' },
      { id: 'rc', x: 230, y: 220, roleLabel: 'Centro dx (13)' },
      { id: 'rw', x: 300, y: 180, roleLabel: 'Ala dx (14)' },
      { id: 'fb', x: 170, y: 100, roleLabel: 'Estremo (15)' },
    ],
  },
  // ─── Basket ──────────────────────────────────────────────────────────────
  {
    id: 'basket_standard_5',
    label: 'Basket 5',
    sport: 'basket',
    positions: [
      { id: 'pg', x: 170, y: 230, roleLabel: 'Playmaker' },
      { id: 'sg', x: 80, y: 180, roleLabel: 'Guardia' },
      { id: 'sf', x: 260, y: 180, roleLabel: 'Ala piccola' },
      { id: 'pf', x: 120, y: 100, roleLabel: 'Ala grande' },
      { id: 'c', x: 220, y: 100, roleLabel: 'Centro' },
    ],
  },
  // ─── Volleyball ─────────────────────────────────────────────────────────
  {
    id: 'volley_6_2',
    label: 'Volley 6-2',
    sport: 'volleyball',
    positions: [
      { id: 'p4', x: 60, y: 180, roleLabel: 'P4 attaccante ala' },
      { id: 'p3', x: 170, y: 180, roleLabel: 'P3 centrale' },
      { id: 'p2', x: 280, y: 180, roleLabel: 'P2 alzatore' },
      { id: 'p5', x: 60, y: 230, roleLabel: 'P5 libero' },
      { id: 'p6', x: 170, y: 230, roleLabel: 'P6 difensore' },
      { id: 'p1', x: 280, y: 230, roleLabel: 'P1 battitore' },
    ],
  },
  // ─── Futsal ─────────────────────────────────────────────────────────────
  {
    id: 'futsal_1_2_1',
    label: 'Futsal 1-2-1',
    sport: 'futsal',
    positions: [
      { id: 'gk', x: 170, y: 240, roleLabel: 'Portiere' },
      { id: 'fix', x: 170, y: 190, roleLabel: 'Ultimo' },
      { id: 'wing_r', x: 280, y: 130, roleLabel: 'Ala dx' },
      { id: 'wing_l', x: 60, y: 130, roleLabel: 'Ala sx' },
      { id: 'piv', x: 170, y: 60, roleLabel: 'Pivot' },
    ],
  },
];

export function getTemplatesForSport(
  sport: FormationTemplate['sport'],
): FormationTemplate[] {
  return FORMATION_TEMPLATES.filter((t) => t.sport === sport);
}
